import React, { useState } from 'react';
import {
  Box, Typography, Card, CardContent, Stepper, Step, StepLabel,
  Button, TextField, FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress, Grid, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, List, ListItem, ListItemText,
  ListItemIcon, Switch, IconButton, Tooltip, RadioGroup,
  FormControlLabel, Radio, Divider
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Wifi as WifiIcon,
  Videocam as CameraIcon,
  Security as SecurityIcon,
  PlayArrow as PlayIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Cloud as CloudIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import UniFiCloudAPIService from './UniFiCloudAPIService';

// UniFi API Client Class
class UniFiAPIClient {
  constructor(config) {
    this.config = config;
    this.baseURL = `https://${config.host}:${config.port}`;
    this.sessionId = null;
  }

  async login() {
    try {
      const response = await fetch(`${this.baseURL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: this.config.username,
          password: this.config.password,
        }),
      });

      if (!response.ok) {
        throw new Error('Login failed');
      }

      const data = await response.json();
      this.sessionId = data.sessionId;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getSites() {
    try {
      const response = await fetch(`${this.baseURL}/api/self/sites`, {
        headers: {
          'Authorization': `Bearer ${this.sessionId}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch sites');
      }

      const data = await response.json();
      return { success: true, data: data.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getDevices(site = 'default') {
    try {
      const response = await fetch(`${this.baseURL}/api/s/${site}/stat/device`, {
        headers: {
          'Authorization': `Bearer ${this.sessionId}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch devices');
      }

      const data = await response.json();
      return { success: true, data: data.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getAccessDevices(site = 'default') {
    try {
      const response = await fetch(`${this.baseURL}/api/s/${site}/access/device`, {
        headers: {
          'Authorization': `Bearer ${this.sessionId}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch access devices');
      }

      const data = await response.json();
      return { success: true, data: data.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async getProtectCameras() {
    try {
      const response = await fetch(`${this.baseURL}/api/protect/cameras`, {
        headers: {
          'Authorization': `Bearer ${this.sessionId}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cameras');
      }

      const data = await response.json();
      return { success: true, data: data.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

const UniFiSetupWizard = ({ onComplete, onCancel }) => {
  const { translate } = useLanguage();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [connectionType, setConnectionType] = useState('controller'); // 'cloud' or 'controller'
  
  const [config, setConfig] = useState({
    // Cloud config
    apiKey: '',
    site: 'default',
    
    // Controller config
    host: '',
    port: 443,
    username: '',
    password: '',
  });
  const [testResults, setTestResults] = useState({});
  const [availableSites, setAvailableSites] = useState([]);
  const [discoveredDevices, setDiscoveredDevices] = useState({
    network: [],
    access: [],
    protect: []
  });

  const steps = [
    translate('unifiSetup.connectionType') || 'Connection Type',
    translate('unifiSetup.connectionSetup') || 'Connection Setup',
    translate('unifiSetup.authentication') || 'Authentication',
    translate('unifiSetup.siteSelection') || 'Site Selection',
    translate('unifiSetup.deviceDiscovery') || 'Device Discovery',
    translate('unifiSetup.testConnection') || 'Test Connection',
    translate('unifiSetup.complete') || 'Complete'
  ];

  const testConnection = async () => {
    setLoading(true);
    
    try {
      if (connectionType === 'cloud') {
        const cloudService = new UniFiCloudAPIService();
        cloudService.initialize(config);
        
        const connectionResult = await cloudService.testConnection();
        if (!connectionResult.success) {
          throw new Error(connectionResult.error);
        }

        const sitesResult = await cloudService.getSites();
        if (sitesResult.success) {
          setAvailableSites(sitesResult.data);
        }

        setTestResults({
          connection: { success: true, message: translate('unifiSetup.connectionSuccess') || 'Connection successful' },
          authentication: { success: true, message: translate('unifiSetup.authSuccess') || 'Authentication successful' },
          sites: sitesResult.success ? { success: true, message: `${sitesResult.data.length} sites found` } : { success: false, message: sitesResult.error }
        });
      } else {
        const client = new UniFiAPIClient(config);
        
        const loginResult = await client.login();
        if (!loginResult.success) {
          throw new Error(loginResult.error);
        }

        const sitesResult = await client.getSites();
        if (sitesResult.success) {
          setAvailableSites(sitesResult.data);
        }

        setTestResults({
          connection: { success: true, message: translate('unifiSetup.connectionSuccess') || 'Connection successful' },
          authentication: { success: true, message: translate('unifiSetup.authSuccess') || 'Authentication successful' },
          sites: sitesResult.success ? { success: true, message: `${sitesResult.data.length} sites found` } : { success: false, message: sitesResult.error }
        });
      }

      return true;
    } catch (error) {
      setTestResults({
        connection: { success: false, message: error.message },
        authentication: { success: false, message: error.message },
        sites: { success: false, message: error.message }
      });
      return false;
    } finally {
      setLoading(false);
    }
  };

  const discoverDevices = async () => {
    setLoading(true);
    const client = new UniFiAPIClient(config);
    
    try {
      await client.login();
      
      const networkResult = await client.getDevices(config.site);
      if (networkResult.success) {
        setDiscoveredDevices(prev => ({
          ...prev,
          network: networkResult.data
        }));
      }

      const accessResult = await client.getAccessDevices(config.site);
      if (accessResult.success) {
        setDiscoveredDevices(prev => ({
          ...prev,
          access: accessResult.data
        }));
      }

      const protectResult = await client.getProtectCameras();
      if (protectResult.success) {
        setDiscoveredDevices(prev => ({
          ...prev,
          protect: protectResult.data
        }));
      }

    } catch (error) {
      console.error('Device discovery failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      // Connection type selection - just move to next step
      setActiveStep(1);
    } else if (activeStep === 1) {
      // Connection setup - validate and move to next
      if (connectionType === 'cloud') {
        if (!config.apiKey.trim()) {
          alert(translate('unifiSetup.apiKeyRequired') || 'Please enter your UniFi Cloud API key');
          return;
        }
      } else {
        if (!config.host || !config.username || !config.password) {
          alert(translate('unifiSetup.fillRequiredFields') || 'Please fill in all required fields');
          return;
        }
      }
      setActiveStep(2);
    } else if (activeStep === 2) {
      // Authentication - test connection
      const success = await testConnection();
      if (success) {
        setActiveStep(3);
      }
    } else if (activeStep === 3) {
      // Site selection - discover devices
      await discoverDevices();
      setActiveStep(4);
    } else if (activeStep === 4) {
      // Device discovery - test everything
      setActiveStep(5);
    } else if (activeStep === 5) {
      // Complete setup
      handleComplete();
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleComplete = () => {
    const finalConfig = {
      ...config,
      connectionType,
      lastUpdated: new Date().toISOString(),
    };

    if (connectionType === 'cloud') {
      localStorage.setItem('unifiCloudConfig', JSON.stringify(finalConfig));
    } else {
      localStorage.setItem('unifiConfig', JSON.stringify(finalConfig));
    }

    onComplete(finalConfig);
  };

  const updateConfig = (field, value) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {translate('unifiSetup.chooseConnectionType')}
            </Typography>
            <RadioGroup
              value={connectionType}
              onChange={(e) => setConnectionType(e.target.value)}
            >
              <FormControlLabel
                value="cloud"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudIcon color="primary" />
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        UniFi Cloud
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {translate('unifiSetup.cloudDescription')}
                      </Typography>
                      <Alert severity="warning" sx={{ mt: 1, fontSize: '0.75rem' }}>
                        ⚠️ Requires backend proxy due to CORS restrictions
                      </Alert>
                    </Box>
                  </Box>
                }
              />
              <FormControlLabel
                value="controller"
                control={<Radio />}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StorageIcon color="primary" />
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        UniFi Controller
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {translate('unifiSetup.controllerDescription')}
                      </Typography>
                    </Box>
                  </Box>
                }
              />
            </RadioGroup>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              {connectionType === 'cloud' ? translate('unifiSetup.cloudSetup') : translate('unifiSetup.controllerSetup')}
            </Typography>
            
            {connectionType === 'cloud' ? (
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={translate('unifiSetup.apiKey')}
                    value={config.apiKey}
                    onChange={(e) => updateConfig('apiKey', e.target.value)}
                    type={showApiKey ? 'text' : 'password'}
                    required
                    helperText={translate('unifiSetup.apiKeyHelper')}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowApiKey(!showApiKey)}
                          edge="end"
                        >
                          {showApiKey ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={translate('unifiSetup.site')}
                    value={config.site}
                    onChange={(e) => updateConfig('site', e.target.value)}
                    helperText={translate('unifiSetup.siteDescription')}
                  />
                </Grid>
              </Grid>
            ) : (
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={translate('unifiSetup.controllerHost')}
                    value={config.host}
                    onChange={(e) => updateConfig('host', e.target.value)}
                    placeholder="192.168.1.1 or controller.example.com"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={translate('unifiSetup.controllerPort')}
                    value={config.port}
                    onChange={(e) => updateConfig('port', parseInt(e.target.value))}
                    type="number"
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={translate('unifiSetup.username')}
                    value={config.username}
                    onChange={(e) => updateConfig('username', e.target.value)}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={translate('unifiSetup.password')}
                    value={config.password}
                    onChange={(e) => updateConfig('password', e.target.value)}
                    type={showPassword ? 'text' : 'password'}
                    required
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Test Connection
            </Typography>
            <Button
              variant="contained"
              onClick={testConnection}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              {loading ? 'Testing...' : 'Test Connection'}
            </Button>
            
            {Object.keys(testResults).length > 0 && (
              <Box sx={{ mt: 2 }}>
                {Object.entries(testResults).map(([key, result]) => (
                  <Alert
                    key={key}
                    severity={result.success ? 'success' : 'error'}
                    sx={{ mb: 1 }}
                    icon={result.success ? <CheckIcon /> : <ErrorIcon />}
                  >
                    {result.message}
                  </Alert>
                ))}
              </Box>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Select Site
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Site</InputLabel>
              <Select
                value={config.site}
                onChange={(e) => updateConfig('site', e.target.value)}
                label="Site"
              >
                {availableSites.map((site) => (
                  <MenuItem key={site.name} value={site.name}>
                    {site.desc || site.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        );

      case 4:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Device Discovery
            </Typography>
            <Button
              variant="contained"
              onClick={discoverDevices}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
            >
              {loading ? 'Discovering...' : 'Discover Devices'}
            </Button>
            
            {Object.keys(discoveredDevices).some(key => discoveredDevices[key].length > 0) && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Discovered Devices:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" color="primary">
                          <WifiIcon /> Network ({discoveredDevices.network.length})
                        </Typography>
                        <List dense>
                          {discoveredDevices.network.slice(0, 3).map((device, index) => (
                            <ListItem key={index}>
                              <ListItemText primary={device.name || device.mac} />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" color="primary">
                          <SecurityIcon /> Access ({discoveredDevices.access.length})
                        </Typography>
                        <List dense>
                          {discoveredDevices.access.slice(0, 3).map((device, index) => (
                            <ListItem key={index}>
                              <ListItemText primary={device.name || device.mac} />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" color="primary">
                          <CameraIcon /> Protect ({discoveredDevices.protect.length})
                        </Typography>
                        <List dense>
                          {discoveredDevices.protect.slice(0, 3).map((device, index) => (
                            <ListItem key={index}>
                              <ListItemText primary={device.name || device.mac} />
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        );

      case 5:
        return (
          <Box>
            <Typography variant="h6" gutterBottom>
              Setup Complete
            </Typography>
            <Alert severity="success" icon={<CheckIcon />}>
              UniFi {connectionType === 'cloud' ? 'Cloud' : 'Controller'} connection configured successfully!
            </Alert>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                You can now manage your UniFi devices, access control, and cameras from your HarvestAI dashboard.
              </Typography>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog
      open={true}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SettingsIcon color="primary" />
          <Typography variant="h6">
            UniFi {connectionType === 'cloud' ? 'Cloud' : 'Controller'} Setup Wizard
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onCancel} color="inherit">
          {translate('common.cancel')}
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack}>
            {translate('common.back')}
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={loading || 
            (activeStep === 1 && connectionType === 'cloud' && !config.apiKey.trim()) ||
            (activeStep === 1 && connectionType === 'controller' && (!config.host || !config.username || !config.password))
          }
        >
          {activeStep === steps.length - 1 ? translate('unifiSetup.finish') : translate('common.next')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UniFiSetupWizard; 